// ---------------------------------------------------------------------------
// TZ Scheduling — data-layer client (OPUS-D13)
// ---------------------------------------------------------------------------
// Thin typed wrapper over `apiClient` for the `/api/v1/campaign-tz-scheduling`
// surface (feature #75). Mirrors the contract declared in
// `apps/api/src/routes/campaign-tz-scheduling.ts`.
//
// The contract is a per-(campaign, affiliate) IANA timezone row. An
// affiliate_id of `*` acts as the campaign-wide default. The resolve endpoint
// takes a UTC timestamp and returns the local hour / day / tz with provenance
// ("affiliate", "campaign_default", "fallback").
// ---------------------------------------------------------------------------
import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Domain types (shape-matched to the API router).
// ---------------------------------------------------------------------------

export const CAMPAIGN_DEFAULT_AFFILIATE = "*" as const;

export interface TzRuleView {
	id: string;
	campaign_id: string;
	affiliate_id: string;
	tz: string;
	created_at: number;
	updated_at: number;
}

export interface ListTzResponse {
	campaign_id: string;
	scheduling: TzRuleView[];
}

export interface UpsertTzResponse {
	scheduling: TzRuleView;
}

export type TzSource = "affiliate" | "campaign_default" | "fallback";

export interface ResolveTzResponse {
	campaign_id: string;
	affiliate_id: string;
	timestamp: number;
	localHour: number;
	localDay: number;
	localTz: string;
	localMinute: number;
	localYear: number;
	localMonth: number;
	localDate: number;
	source: TzSource;
}

// ---------------------------------------------------------------------------
// Curated IANA timezone list — the combobox surfaces these by default; the
// free-text input still accepts any IANA zone the browser's Intl stack
// recognises. The curated set covers the regions Trackerity operates in
// (Americas / EMEA / APAC) + UTC for the pedantic operator.
// ---------------------------------------------------------------------------

export const CURATED_TIMEZONES: readonly string[] = [
	"UTC",
	"America/Los_Angeles",
	"America/Denver",
	"America/Chicago",
	"America/New_York",
	"America/Sao_Paulo",
	"Europe/London",
	"Europe/Dublin",
	"Europe/Berlin",
	"Europe/Paris",
	"Europe/Madrid",
	"Europe/Amsterdam",
	"Europe/Stockholm",
	"Europe/Warsaw",
	"Europe/Istanbul",
	"Africa/Johannesburg",
	"Asia/Dubai",
	"Asia/Karachi",
	"Asia/Kolkata",
	"Asia/Bangkok",
	"Asia/Singapore",
	"Asia/Hong_Kong",
	"Asia/Shanghai",
	"Asia/Tokyo",
	"Asia/Seoul",
	"Australia/Perth",
	"Australia/Sydney",
	"Pacific/Auckland",
];

/**
 * Cheap runtime check — leans on `Intl.DateTimeFormat` which will throw for
 * unknown zones. Returns the normalized tz or null when it cannot be parsed.
 */
export function normalizeTimezoneClient(raw: string | null | undefined): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > 64) return null;
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: trimmed });
		return trimmed;
	} catch {
		return null;
	}
}

// ---------------------------------------------------------------------------
// Form input + parse result (hand-rolled schema, no external zod dep to
// stay inside the scaffold dependency lock). The shape mimics zod so the
// UI layer reads familiarly.
// ---------------------------------------------------------------------------

export interface TzFormInput {
	campaignId: string;
	affiliateId: string;
	tz: string;
}

export interface TzFormParsed {
	campaignId: string;
	affiliateId: string;
	tz: string;
}

export type ParseResult<T> =
	| { success: true; data: T }
	| { success: false; errors: Record<string, string> };

const ID_MAX = 256;

export const tzFormSchema = {
	safeParse(input: TzFormInput): ParseResult<TzFormParsed> {
		const errors: Record<string, string> = {};

		const campaignId = (input.campaignId ?? "").trim();
		if (campaignId.length === 0) {
			errors.campaignId = "Campaign ID is required.";
		} else if (campaignId.length > ID_MAX) {
			errors.campaignId = `Campaign ID must be ${ID_MAX} chars or fewer.`;
		} else if (/[\x00-\x1f]/.test(campaignId)) {
			errors.campaignId = "Campaign ID contains invalid control characters.";
		}

		const affRaw = (input.affiliateId ?? "").trim();
		const affiliateId = affRaw.length === 0 ? CAMPAIGN_DEFAULT_AFFILIATE : affRaw;
		if (affiliateId.length > ID_MAX) {
			errors.affiliateId = `Affiliate ID must be ${ID_MAX} chars or fewer.`;
		} else if (/[\x00-\x1f]/.test(affiliateId)) {
			errors.affiliateId = "Affiliate ID contains invalid control characters.";
		}

		const tz = normalizeTimezoneClient(input.tz);
		if (!tz) {
			errors.tz = "Pick a valid IANA timezone (e.g. America/New_York).";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: { campaignId, affiliateId, tz: tz as string },
		};
	},
};

// ---------------------------------------------------------------------------
// Resolve form input — timestamp arrives as an ISO local-datetime string
// from <input type="datetime-local">; convert to unix-ms for the POST body.
// ---------------------------------------------------------------------------

export interface ResolveFormInput {
	campaignId: string;
	affiliateId: string;
	utcDatetimeLocal: string;
}

export interface ResolveFormParsed {
	campaignId: string;
	affiliateId: string;
	timestampMs: number;
}

export const resolveFormSchema = {
	safeParse(input: ResolveFormInput): ParseResult<ResolveFormParsed> {
		const errors: Record<string, string> = {};

		const campaignId = (input.campaignId ?? "").trim();
		if (campaignId.length === 0) {
			errors.campaignId = "Campaign ID is required.";
		}

		const affRaw = (input.affiliateId ?? "").trim();
		const affiliateId = affRaw.length === 0 ? CAMPAIGN_DEFAULT_AFFILIATE : affRaw;

		let timestampMs = NaN;
		const raw = (input.utcDatetimeLocal ?? "").trim();
		if (raw.length === 0) {
			errors.utcDatetimeLocal = "Pick a UTC timestamp to resolve.";
		} else {
			// datetime-local value is "YYYY-MM-DDTHH:mm" with no timezone.
			// The user intends this as a UTC moment, so append 'Z'.
			const iso = /Z$/i.test(raw) ? raw : `${raw}Z`;
			const parsed = Date.parse(iso);
			if (!Number.isFinite(parsed)) {
				errors.utcDatetimeLocal = "Invalid datetime — use the picker.";
			} else {
				timestampMs = parsed;
			}
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: { campaignId, affiliateId, timestampMs },
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations — TanStack Query consumers import these directly.
// ---------------------------------------------------------------------------

const BASE = "/campaign-tz-scheduling";

export async function listTzRules(
	campaignId: string,
	signal?: AbortSignal,
): Promise<TzRuleView[]> {
	if (!campaignId.trim()) return [];
	try {
		const res = await apiClient.get<ListTzResponse>(
			`${BASE}/${encodeURIComponent(campaignId)}`,
			{ signal },
		);
		return Array.isArray(res.scheduling) ? res.scheduling : [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export interface UpsertTzArgs {
	campaignId: string;
	affiliateId: string;
	tz: string;
}

export async function upsertTzRule(args: UpsertTzArgs): Promise<TzRuleView> {
	const res = await apiClient.post<UpsertTzResponse>(BASE, {
		campaignId: args.campaignId,
		affiliateId: args.affiliateId,
		tz: args.tz,
	});
	return res.scheduling;
}

export interface DeleteTzArgs {
	campaignId: string;
	affiliateId: string;
}

export async function deleteTzRule(args: DeleteTzArgs): Promise<void> {
	await apiClient.delete(
		`${BASE}/${encodeURIComponent(args.campaignId)}/affiliate/${encodeURIComponent(args.affiliateId)}`,
	);
}

export interface ResolveTzArgs {
	campaignId: string;
	affiliateId: string;
	timestampMs: number;
}

export async function resolveTz(args: ResolveTzArgs): Promise<ResolveTzResponse> {
	const res = await apiClient.post<ResolveTzResponse>(
		`${BASE}/${encodeURIComponent(args.campaignId)}/resolve`,
		{
			affiliateId: args.affiliateId,
			// API heuristic: < 1e12 => seconds. We always send milliseconds.
			timestamp: args.timestampMs,
		},
	);
	return res;
}

// ---------------------------------------------------------------------------
// Query keys — exposed so the route component can invalidate on mutation.
// ---------------------------------------------------------------------------

export const tzSchedulingQueryKeys = {
	all: ["tz-scheduling"] as const,
	list: (campaignId: string) =>
		[...tzSchedulingQueryKeys.all, "list", campaignId] as const,
};

// ---------------------------------------------------------------------------
// Presentation helpers — reused by the route + form.
// ---------------------------------------------------------------------------

export function formatTimestamp(epochSeconds: number): string {
	if (!Number.isFinite(epochSeconds) || epochSeconds <= 0) return "—";
	const d = new Date(epochSeconds * 1000);
	const y = d.getUTCFullYear();
	const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
	const da = String(d.getUTCDate()).padStart(2, "0");
	const hh = String(d.getUTCHours()).padStart(2, "0");
	const mm = String(d.getUTCMinutes()).padStart(2, "0");
	return `${y}-${mo}-${da} ${hh}:${mm} UTC`;
}

export function labelAffiliate(affiliateId: string): string {
	return affiliateId === CAMPAIGN_DEFAULT_AFFILIATE
		? "Campaign default"
		: affiliateId;
}

export function describeSource(source: TzSource): string {
	switch (source) {
		case "affiliate":
			return "Affiliate-specific rule.";
		case "campaign_default":
			return "Inherited from the campaign default (*).";
		case "fallback":
			return "No rule matched — defaulted to UTC.";
	}
}
